<template>
  <div class="userlist">
    <div class="user" v-for="user in users" :key="user.handle">
      <router-link :to="userLink(user)"><img :src="userAvatarUrl(user)" v-if="userAvatarUrl(user)"></router-link>
      <i class="sl-icon-user" v-if="!userAvatarUrl(user)"></i>
      <div class="info">
        <div class="name">
          <router-link :to="userLink(user)">{{ userName(user) }}</router-link>
        </div>
        <div class="role" v-if="withRoles">
          {{ user.role }}
        </div>
      </div>
      <div class="remove" v-if="canRemove">
        <LinkButton @click="removeUser(user)" :u="true">
          <span v-if="removeLabel">
            {{ removeLabel }}
          </span>
        </LinkButton>
        <font-awesome-icon icon="times" size="sm" @click="removeUser(user)" v-if="!removeLabel" class="fa-remove"></font-awesome-icon>
      </div>
    </div>

    <div class="user dropdown" v-if="canAdd">
      <i class="sl-icon-user-follow"></i>
      <div class="info">
        <LinkButton class="name" @click="toggleDropdown">
          {{ addLabel || 'Add user' }}
        </LinkButton>
      </div>
      <div class="dropdown-menu" v-if="dropdownVisible">
        <b-form-group :label="handleLabel || 'User name or handle'">
          <b-form-input v-model.trim="handle" type="text" required placeholder="@handle" size="sm" :state="handleState" />
        </b-form-group>
        <b-form-group :label="roleLabel || 'Role'" v-if="withRoles">
          <b-form-select v-model="role" :options="roles" size="sm" />
        </b-form-group>

        <b-button variant="secondary" size="sm" @click="addUser" :disabled="!handle">{{ addLabel || 'Add user' }}</b-button>
        <LinkButton :u="true" @click="closeDropdown" class="cancel">cancel</LinkButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';

  .userlist {
    background-color: #1e1f36;
    border-radius: 4px;
    padding: 1em;

    display: flex;
    flex-wrap: wrap;

    &.vertical {
      flex-direction: column;
      align-items: stretch;
    }

    .user {
      background-color: #343459;
      padding: 0.5em 1em 0.5em 0.5em;
      margin: 0.5em 0.5em;
      display: flex;
      align-items: center;

      i {
        display: block;
        align-self: center;
        border-radius: 100%;
        border: 1px dashed $steel;
        padding: 0.75em;
        background-color: #1e1f36;
      }

      img {
        width: 42px;
        height: 42px;
        border-radius: 100%;
      }
      .info {
        margin-left: 1em;
        margin-right: 2em;
        display: flex;
        flex-direction: column;
        text-align: left;
        justify-content: center;

        .name {
          font-size: 0.8em;
          font-weight: bold;

          a {
            color: $white;
            text-decoration: underline;
          }
        }

        .role {
          font-size: 0.8em;
          color: $steel;
        }
      }

      .remove {
        font-size: 0.8em;

        .fa-remove {
          &:hover {
            color: red;
            cursor: pointer;
          }
        }
      }

      &.dropdown {
        .dropdown-menu {
          right: 0;
          display: block;
          border-radius: 3px;
          background-color: #343459;
          box-shadow: 7px 2px 9px 0 rgba(0, 0, 0, 0.6);
          font-size: 0.8em;
          padding: 1em;

          .cancel {
            margin-left: 1em;
          }
        }
      }
    }
  }

</style>

<script>
import LinkButton from '@/components/widgets/LinkButton'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

import { roles } from '@/store/modules/tag'

export default {
  name: 'UserList',
  components: {
    FontAwesomeIcon,
    LinkButton
  },
  model: {
    prop: 'users',
    event: 'change'
  },
  props: {
    users: Array,
    withRoles: Boolean,
    canAdd: Boolean,
    canRemove: Boolean,
    addLabel: String,
    handleLabel: String,
    roleLabel: String,
    removeLabel: String,
    requireUser: Boolean,
    forceFan: Boolean
  },
  data () {
    return {
      roles: roles,
      handle: '',
      handleState: null,
      role: '',
      dropdownVisible: false
    }
  },
  computed: {
  },
  methods: {
    toggleDropdown () {
      this.dropdownVisible = !this.dropdownVisible
    },
    closeDropdown () {
      this.dropdownVisible = false
      this.handle = ''
      this.role = ''
      this.handleState = null
    },
    async addUser () {
      if (!this.canAdd || !this.handle || (this.withRoles && !this.role)) return

      let entry = {
        handle: this.handle,
        user: null
      }

      this.handleState = null

      if (this.handle[0] === '@') {
        let users = await this.$store.dispatch('profile/findProfile', { handle: this.handle.substr(1) })
        if (users.count) {
          entry.user = users.results[0]
          entry.name = entry.user.name
          this.handleState = 'valid'
        } else {
          this.handleState = 'invalid'
          return
        }
      } else if (this.requireUser) {
        this.handleState = 'invalid'
        return
      }

      if (this.withRoles) {
        entry.role = this.role
      }

      this.closeDropdown()
      this.users.push(entry)
      this.$emit('change', this.users)
    },
    removeUser (user) {
      if (!this.canRemove) return

      let idx = this.users.indexOf(user)
      if (idx === -1) return

      this.users.splice(idx, 1)
      this.$emit('change', this.users)
    },
    isArtist (user) {
      if (!user.user) return false
      return (
        !this.forceFan &&
        !!user.user.artist &&
        user.user.artist.handle.toLowerCase() === user.handle.substr(1).toLowerCase()
      )
    },
    userName (user) {
      if (!user.user) return user.handle
      if (this.isArtist(user)) return user.user.artist.name
      return '@' + user.user.username
    },
    userAvatarUrl (user) {
      if (!user.user) return null
      if (this.isArtist(user)) return user.user.artist.avatar_image
      return user.user.avatar_image
    },
    userLink (user) {
      if (!user.user) return null
      if (!this.forceFan && user.user.artist) return {name: 'artist-about', params: {artistId: user.user.artist.id}}
      return {name: 'fan-profile', params: {id: user.user.id}}
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/widgets/UserList.vue